import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Toast from '../Toast';
import useToast from '../../hooks/useToast';

function ManageTeams() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editTeamId, setEditTeamId] = useState(null);
  const [editNamePt, setEditNamePt] = useState('');
  const [savingId, setSavingId] = useState(null);

  const { toasts, addToast, removeToast } = useToast();

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/teams', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setTeams(response.data);
      setError(null);
    } catch (err) {
      setError('Erro ao carregar seleções');
      console.error('Error fetching teams:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (team) => {
    setEditTeamId(team.id);
    setEditNamePt(team.name_pt || '');
  };

  const handleCancelEdit = () => {
    setEditTeamId(null);
    setEditNamePt('');
  };

  const handleSaveEdit = async (id) => {
    setSavingId(id);
    try {
      await axios.put(`/api/admin/teams/${id}`, { name_pt: editNamePt }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setEditTeamId(null);
      addToast('Seleção atualizada com sucesso!', 'success');
      fetchTeams();
    } catch (err) {
      console.error('Error updating team:', err);
      addToast('Erro ao atualizar a tradução da seleção', 'error');
    } finally {
      setSavingId(null);
    }
  };

  if (loading) return <div className="p-6">Carregando seleções...</div>;
  if (error) return <div className="p-6 text-red-600">{error}</div>;

  return (
    <div className="p-4 lg:p-6">
      <h1 className="text-2xl font-bold mb-6 text-primary">Gerenciar Seleções</h1>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full min-w-[500px] divide-y divide-gray-200">
          <thead className="bg-primary text-white">
            <tr>
              <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">ID</th>
              <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Nome Original (Inglês)</th>
              <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Tradução (Português)</th>
              <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Ações</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {teams.map((team) => (
              <tr key={team.id} className="hover:bg-gray-50">
                <td className="px-3 lg:px-6 py-4 text-sm text-gray-500">
                  {team.id}
                </td>
                <td className="px-3 lg:px-6 py-4 text-sm font-medium text-gray-900 break-words">
                  {team.name_en}
                </td>
                <td className="px-3 lg:px-6 py-4 text-sm text-gray-500">
                  {editTeamId === team.id ? (
                    <input
                      type="text"
                      value={editNamePt}
                      onChange={(e) => setEditNamePt(e.target.value)}
                      className="border border-gray-300 rounded px-3 py-1 focus:ring-green-500 focus:border-green-500 w-full min-h-11"
                      placeholder="Nome em Português"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveEdit(team.id);
                        if (e.key === 'Escape') handleCancelEdit();
                      }}
                    />
                  ) : (
                    <span className={team.name_pt ? 'text-gray-900' : 'text-gray-400 italic'}>
                      {team.name_pt || 'Não traduzido'}
                    </span>
                  )}
                </td>
                <td className="px-3 lg:px-6 py-4 text-sm font-medium">
                  {editTeamId === team.id ? (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSaveEdit(team.id)}
                        disabled={savingId === team.id}
                        className="bg-green-50 hover:bg-green-100 text-green-700 px-3 py-1.5 rounded-md font-bold min-h-11 focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 focus:outline-none disabled:opacity-50"
                      >
                        {savingId === team.id ? 'Salvando...' : 'Salvar'}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="bg-gray-50 hover:bg-gray-100 text-gray-700 px-3 py-1.5 rounded-md min-h-11 focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 focus:outline-none"
                      >
                        Cancelar
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleEditClick(team)}
                      className="bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-1.5 rounded-md min-h-11 focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 focus:outline-none"
                    >
                      Editar Tradução
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {teams.length === 0 && (
              <tr>
                <td colSpan="4" className="px-3 lg:px-6 py-4 text-center text-gray-500">
                  Nenhuma seleção cadastrada ainda. Sincronize as partidas para carregar as seleções.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Toast toasts={toasts} removeToast={removeToast} />
    </div>
  );
}

export default ManageTeams;